/**
 * Das Kundengruppen-Objektblatt (Einrichten-Neubau P5).
 *
 * Endlich echte Mitgliederpflege an der Gruppe: Die Kunden der Gruppe stehen
 * hier, entfernen ist zweistufig, hinzufügen läuft über eine Suche — kein
 * Pingpong mehr über die Kundenliste mit Bulk-Modal. Sammelposten (feste
 * System-Gruppen der WSA-Erfassung) haben keine festen Mitglieder; ihr Blatt
 * erklärt das statt leere Werkzeuge zu zeigen.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../App';
import { LeitstandRahmen } from '../../components/leitstand/LeitstandRahmen';
import { Karte, Kennung, Notiz, Zustand } from '../../components/leitstand';

const EINGABE =
  'rounded-[4px] border border-linie-2 bg-flaeche-2 px-3 py-2 text-sm text-tinte focus:border-akzent focus:outline-none';
const KNOPF = 'rounded-[4px] px-3 py-1.5 font-zahl text-xs disabled:opacity-50';
const KNOPF_PRIMAER = `${KNOPF} border border-akzent bg-akzent text-akzent-satz`;
const KNOPF_LEISE = `${KNOPF} border border-linie-2 bg-flaeche text-tinte-2`;

interface Gruppe {
  id: string;
  name: string;
  type?: string;
  systemCode?: string | null;
  sortOrder?: number;
  isActive: boolean;
}

interface Kunde {
  id: string;
  name: string;
  customerGroupId?: string | null;
  isActive: boolean;
}

export default function GruppeBlatt({
  art = 'kundengruppen',
}: {
  art?: 'kundengruppen' | 'sammelposten';
}) {
  const { user } = useAuth();
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const istNeu = id === 'neu';

  const [g, setG] = useState<Gruppe | null>(null);
  const [name, setName] = useState('');
  const [kunden, setKunden] = useState<Kunde[]>([]);
  const [laden, setLaden] = useState(!istNeu);
  const [fehler, setFehler] = useState('');
  const [aktionsFehler, setAktionsFehler] = useState('');
  const [gespeichert, setGespeichert] = useState('');
  const [speichert, setSpeichert] = useState(false);
  const [schalterFrage, setSchalterFrage] = useState(false);

  const [entferneId, setEntferneId] = useState<string | null>(null);
  const [suchtext, setSuchtext] = useState('');
  const [verlauf, setVerlauf] = useState<Array<{ wann: string; wer: string; was: string }>>([]);

  const istSammelposten = g?.type === 'SYSTEM_BUCKET';

  const lade = useCallback(async () => {
    if (istNeu) return;
    setLaden(true);
    setFehler('');
    try {
      const [oRes, kRes, hRes] = await Promise.all([
        fetch(`/api/einrichten/objekt/${art}/${id}`, { credentials: 'include' }),
        fetch('/api/einrichten/objekt/kunden', { credentials: 'include' }),
        fetch(`/api/einrichten/objekt/${art}/${id}/verlauf`, { credentials: 'include' }),
      ]);
      if (!oRes.ok) throw new Error('Die Gruppe konnte nicht geladen werden');
      const o = (await oRes.json()).objekt as Gruppe;
      setG(o);
      setName(o.name ?? '');
      if (kRes.ok) setKunden(((await kRes.json()).objekte ?? []) as Kunde[]);
      if (hRes.ok) setVerlauf((await hRes.json()).zeilen ?? []);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Die Gruppe konnte nicht geladen werden');
    } finally {
      setLaden(false);
    }
  }, [art, id, istNeu]);

  useEffect(() => {
    void lade();
  }, [lade]);

  const api = async (pfad: string, methode: string, body?: unknown) => {
    setAktionsFehler('');
    const res = await fetch(pfad, {
      method: methode,
      headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
      credentials: 'include',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    const antwort = await res.json().catch(() => null);
    if (!res.ok) throw new Error(antwort?.error || 'Aktion fehlgeschlagen');
    return antwort;
  };

  const melde = (text: string) => {
    setGespeichert(text);
    setTimeout(() => setGespeichert(''), 2600);
  };

  const speichere = async () => {
    if (!name.trim()) {
      setAktionsFehler('Bitte einen Namen angeben.');
      return;
    }
    setSpeichert(true);
    try {
      if (istNeu) {
        const antwort = await api('/api/admin/customer-groups', 'POST', { name: name.trim() });
        const neueId = antwort?.id ?? antwort?.group?.id;
        navigate(neueId ? `/einrichten/${art}/${neueId}` : `/einrichten/${art}`, { replace: true });
        return;
      }
      await api(`/api/admin/customer-groups/${id}`, 'PATCH', { name: name.trim() });
      melde('✓ Gespeichert');
      void lade();
    } catch (e) {
      setAktionsFehler(e instanceof Error ? e.message : 'Speichern fehlgeschlagen');
    } finally {
      setSpeichert(false);
    }
  };

  const setzeKundenGruppe = async (kunde: Kunde, gruppeId: string | null, erfolg: string) => {
    try {
      await api(`/api/admin/customers/${kunde.id}`, 'PATCH', { customerGroupId: gruppeId });
      setEntferneId(null);
      setSuchtext('');
      melde(erfolg);
      void lade();
    } catch (e) {
      setAktionsFehler(e instanceof Error ? e.message : 'Aktion fehlgeschlagen');
    }
  };

  const schalteZustand = async (ziel: boolean) => {
    try {
      await api(`/api/einrichten/objekt/${art}/${id}/zustand`, 'POST', { aktiv: ziel });
      setSchalterFrage(false);
      void lade();
    } catch (e) {
      setAktionsFehler(e instanceof Error ? e.message : 'Zustand konnte nicht geändert werden');
    }
  };

  // Sammelposten sind fest eingerichtete System-Töpfe — kein Anlegen über die URL
  if (istNeu && art === 'sammelposten') {
    return (
      <LeitstandRahmen titel="Sammelposten" benutzer={user?.displayName}>
        <Notiz titel="Sammelposten sind fest eingerichtet">
          Die Sortier-Töpfe der WSA-Erfassung werden nicht von Hand angelegt.{' '}
          <Link to="/einrichten/sammelposten" className="underline">
            Zur Übersicht
          </Link>
        </Notiz>
      </LeitstandRahmen>
    );
  }

  const mitglieder = kunden.filter((k) => k.customerGroupId === id);
  const begriff = suchtext.trim().toLowerCase();
  const kandidaten =
    begriff.length >= 2
      ? kunden
          .filter(
            (k) => k.customerGroupId !== id && k.isActive && k.name.toLowerCase().includes(begriff),
          )
          .slice(0, 8)
      : [];

  return (
    <LeitstandRahmen
      titel={istNeu ? 'Kundengruppe anlegen' : name || 'Kundengruppe'}
      benutzer={user?.displayName}
      werkzeuge={
        !istNeu && g ? (
          <span className="flex items-center gap-3">
            {istSammelposten && <Kennung>Sammelposten</Kennung>}
            {g.isActive ? (
              <Zustand art="gut">aktiv</Zustand>
            ) : (
              <span className="font-zahl text-[11px] text-tinte-3">außer Betrieb</span>
            )}
            {!istSammelposten && (
              <button
                onClick={() => (g.isActive ? setSchalterFrage(true) : void schalteZustand(true))}
                className={KNOPF_LEISE}
              >
                {g.isActive ? 'Außer Betrieb nehmen' : 'Wieder in Betrieb nehmen'}
              </button>
            )}
          </span>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-6">
        <Link to={`/einrichten/${art}`} className="text-[13px] text-tinte-3 hover:text-tinte-2">
          ← {art === 'sammelposten' ? 'Sammelposten' : 'Kundengruppen'}
        </Link>

        {schalterFrage && g && (
          <div className="flex flex-wrap items-center gap-3 rounded-leitstand border border-linie-2 bg-flaeche px-4 py-3">
            <span className="text-[13.5px] text-tinte-2">
              Die Gruppe verschwindet dann aus der Auswahl an den Stationen. Die {mitglieder.length}{' '}
              Kunden und alles Erfasste bleiben unverändert.
            </span>
            <button onClick={() => void schalteZustand(false)} className={KNOPF_PRIMAER}>
              Außer Betrieb nehmen
            </button>
            <button onClick={() => setSchalterFrage(false)} className={KNOPF_LEISE}>
              Abbrechen
            </button>
          </div>
        )}

        {fehler && (
          <Notiz titel="Konnte nicht laden">
            {fehler}{' '}
            <button onClick={() => void lade()} className="underline">
              Erneut versuchen
            </button>
          </Notiz>
        )}

        {laden && !fehler && (
          <p className="py-12 text-center text-sm text-tinte-3">Lade Gruppe …</p>
        )}

        {!laden && !fehler && (
          <>
            <Karte titel="Stammdaten" marke="ein Speichern, eine Rückmeldung">
              <div className="flex flex-col gap-4">
                <div className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
                  <label className="flex flex-col gap-1.5">
                    <span className="font-zahl text-[10px] uppercase tracking-[0.13em] text-tinte-3">
                      Name *
                    </span>
                    <input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className={`${EINGABE} w-full`}
                    />
                  </label>
                </div>
                {aktionsFehler && <p className="text-[13px] text-stoer">{aktionsFehler}</p>}
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => void speichere()}
                    disabled={speichert}
                    className={KNOPF_PRIMAER}
                  >
                    {istNeu ? 'Anlegen' : 'Speichern'}
                  </button>
                  {gespeichert && (
                    <span className="font-zahl text-[13px] text-lauf">{gespeichert}</span>
                  )}
                </div>
                {istSammelposten && g?.systemCode && (
                  <details className="text-[12.5px] text-tinte-3">
                    <summary className="cursor-pointer font-zahl text-[11px] uppercase tracking-[0.13em]">
                      Technische Angaben
                    </summary>
                    <p className="mt-1.5">
                      System-Kürzel <span className="font-zahl text-tinte-2">{g.systemCode}</span> —
                      die WSA-Erfassung hängt daran, deshalb unveränderlich.
                    </p>
                  </details>
                )}
              </div>
            </Karte>

            {!istNeu &&
              (istSammelposten ? (
                <Karte titel="Mitglieder" marke="Sammelposten funktionieren anders">
                  <p className="py-2 text-sm text-tinte-2">
                    Ein Sammelposten hat keine feste Mitgliederliste — die Wäscher wählen die
                    beteiligten Kunden je Waschgang direkt an der Station.
                  </p>
                </Karte>
              ) : (
                <Karte
                  titel="Mitglieder"
                  marke={`${mitglieder.length} Kunden — gepflegt wird hier`}
                >
                  <div className="flex flex-col gap-3">
                    {mitglieder.length === 0 ? (
                      <p className="py-3 text-center text-sm text-tinte-3">
                        Noch keine Kunden in dieser Gruppe.
                      </p>
                    ) : (
                      <div className="flex flex-col">
                        {mitglieder.map((k, i) => (
                          <div
                            key={k.id}
                            className={`flex flex-wrap items-center gap-3 px-1 py-2.5 ${i < mitglieder.length - 1 ? 'border-b border-linie' : ''}`}
                          >
                            <Link
                              to={`/einrichten/kunden/${k.id}`}
                              className={`text-sm hover:underline ${k.isActive ? '' : 'text-tinte-3 line-through'}`}
                            >
                              {k.name}
                            </Link>
                            {entferneId === k.id ? (
                              <span className="ml-auto flex items-center gap-2">
                                <span className="text-[13px] text-tinte-2">
                                  Aus der Gruppe nehmen?
                                </span>
                                <button
                                  onClick={() => void setzeKundenGruppe(k, null, '✓ Entfernt')}
                                  className={KNOPF_PRIMAER}
                                >
                                  Ja
                                </button>
                                <button onClick={() => setEntferneId(null)} className={KNOPF_LEISE}>
                                  Nein
                                </button>
                              </span>
                            ) : (
                              <button
                                onClick={() => setEntferneId(k.id)}
                                className={`ml-auto ${KNOPF_LEISE}`}
                              >
                                Entfernen
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="flex flex-col gap-2 border-t border-linie pt-3">
                      <input
                        value={suchtext}
                        onChange={(e) => setSuchtext(e.target.value)}
                        placeholder="Kunden suchen und hinzufügen …"
                        className={`${EINGABE} w-full sm:max-w-sm`}
                      />
                      {kandidaten.map((k) => (
                        <button
                          key={k.id}
                          onClick={() => void setzeKundenGruppe(k, id, `✓ ${k.name} hinzugefügt`)}
                          className="flex items-center gap-2 rounded-[4px] px-2 py-1.5 text-left text-sm hover:bg-flaeche-2"
                        >
                          <span className="font-zahl text-tinte-3">+</span> {k.name}
                        </button>
                      ))}
                      {begriff.length >= 2 && kandidaten.length === 0 && (
                        <span className="text-xs text-tinte-3">
                          Kein passender Kunde außerhalb der Gruppe.
                        </span>
                      )}
                    </div>
                  </div>
                </Karte>
              ))}

            {!istNeu && (
              <Karte titel="Verlauf" marke="aus dem Protokoll, neueste zuerst">
                {verlauf.length === 0 ? (
                  <p className="py-4 text-center text-sm text-tinte-3">
                    Noch keine Einträge im Protokoll.
                  </p>
                ) : (
                  <div className="flex flex-col">
                    {verlauf.map((z, i) => (
                      <div
                        key={`${z.wann}-${i}`}
                        className={`grid grid-cols-[110px_130px_1fr] gap-3 px-1 py-2.5 text-[13px] max-sm:grid-cols-[90px_1fr] ${
                          i < verlauf.length - 1 ? 'border-b border-linie' : ''
                        }`}
                      >
                        <span className="font-zahl text-xs text-tinte-3">
                          {new Date(z.wann).toLocaleString('de-DE', {
                            timeZone: 'Europe/Berlin',
                            day: '2-digit',
                            month: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                        <span className="text-tinte-2">{z.wer}</span>
                        <span className="max-sm:col-span-2">{z.was}</span>
                      </div>
                    ))}
                  </div>
                )}
              </Karte>
            )}
          </>
        )}
      </div>
    </LeitstandRahmen>
  );
}
